import { useState } from 'react'
import { useReportBusy } from '@/console/state/useReportBusy'
import { Callout } from '@/console/ui/Callout'
import { Note } from '@/console/ui/Note'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { GlobalReconfirmSection } from './GlobalReconfirmSection'
import { GlobalRollbackExecution } from './GlobalRollbackExecution'
import { GlobalRollbackFlow, ROLLBACK_REQUEST_KIND, useGlobalRollbackFlow } from './GlobalRollbackFlow'
import { GlobalRollbackInput, GlobalRollbackProgress } from './GlobalRollbackInput'
import { GlobalRollbackExecuteAction, GlobalRollbackJudgement } from './GlobalRollbackJudgement'

const RollbackSection = ({ flow }: { flow: GlobalRollbackFlow }): JSX.Element => (
  <section>
    <SectionHeading
      id="s-inb-global-rollback"
      title="글로벌 입고 롤백"
      badge={<SourceBadge tone="new">신규 · POST /confirm-global-inbounds/rollback</SourceBadge>}
    />
    <Stack>
      <GlobalRollbackInput flow={flow} />
      <GlobalRollbackProgress inFlight={flow.inFlight} />
      {flow.error ? <Callout tone="crit">{flow.error.message}</Callout> : null}
      <GlobalRollbackJudgement flow={flow} />
      <GlobalRollbackExecuteAction flow={flow} />
      <GlobalRollbackExecution flow={flow} />
    </Stack>
    <Note>
      판정은 아무것도 바꾸지 않아요. 되돌릴 수 없는 문서가 대부분인 것이 정상이에요. <b>MOMS_ENG · MUSINSA12 · MUSINSA0 · MASTER</b> 중 하나 필요.
    </Note>
  </section>
)

type ReconfirmSectionProps = {
  flow: GlobalRollbackFlow
  rollbackExecuting: boolean
  onSubmittingChange: (submitting: boolean) => void
}

const ReconfirmSection = ({ flow, rollbackExecuting, onSubmittingChange }: ReconfirmSectionProps): JSX.Element => (
  <section>
    <SectionHeading
      id="s-inb-global-reconfirm"
      title="글로벌 재확정"
      badge={<SourceBadge tone="have">기존 · POST /confirm-global-inbounds</SourceBadge>}
    />
    <GlobalReconfirmSection
      rollbackExecuting={rollbackExecuting}
      reconfirmNeeded={flow.reconfirmNeeded}
      serverMayStillBeProcessing={flow.serverMayStillBeProcessing}
      onReconfirmed={flow.markReconfirmed}
      onSubmittingChange={onSubmittingChange}
    />
  </section>
)

/**
 * 글로벌 입고 롤백(`s-inb-global-rollback`) → 재확정(`s-inb-global-reconfirm`).
 * 롤백 실행 중에는 재확정을, 재확정 중에는 롤백 실행을 잠가야 하므로 두 섹션을 한 패널이 함께 든다.
 */
export const GlobalRollbackPanel = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const [reconfirming, setReconfirming] = useState(false)
  const flow = useGlobalRollbackFlow({ executionLocked: reconfirming })
  const rollbackExecuting = flow.inFlight?.kind === ROLLBACK_REQUEST_KIND.EXECUTING
  useReportBusy(rollbackExecuting || reconfirming, onBusyChange)

  return (
    <>
      <RollbackSection flow={flow} />
      <ReconfirmSection flow={flow} rollbackExecuting={rollbackExecuting} onSubmittingChange={setReconfirming} />
    </>
  )
}
